<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
// IMPORTANT: stores/composable imported BY FILE PATH, never via '@/features/aiassistant'
// (the root barrel statically exports AiAssistantView → would fold the page +
// markdown chain into DefaultLayout's eager chunk).
import { useAiAvailability } from '../composables/useAiPanel'
import { useAiPanelStore } from '../stores/ai-panel.store'
import { useAiChatStore } from '../stores/ai-chat.store'

/**
 * DEV-7125 — the bottom-right "Ask AI Assistant" FAB. Dumb by design: click does
 * exactly one thing (panel.open()) — the drawer's isOpen watcher performs the
 * per-screen restore (single orchestration site, see useAiPanel).
 */
interface Props {
  /** True while a tour overlay owns the screen (Explore panel z3219/3220 or a
   *  spotlight run — opening the drawer mid-tour shifts v-main under the spotlight). */
  suppressed?: boolean
}
const props = withDefaults(defineProps<Props>(), { suppressed: false })

const { isAvailable } = useAiAvailability()
const panel = useAiPanelStore()
const { isStreaming } = storeToRefs(useAiChatStore())

const showFab = computed(() => isAvailable.value && !panel.isOpen && !props.suppressed)
</script>

<template>
  <Transition name="scale-fade">
    <div
      v-if="showFab"
      class="ai-fab"
      data-test="ai-fab"
    >
      <v-btn
        icon
        size="large"
        color="primary"
        elevation="8"
        aria-label="Ask AI Assistant"
        data-test="ai-fab-btn"
        @click="panel.open()"
      >
        <v-icon icon="fa-solid fa-wand-magic-sparkles" />
        <span
          v-if="isStreaming"
          class="ai-fab__pulse"
          data-test="ai-fab-pulse"
        />
        <!-- Tooltip INSIDE the v-btn: activator="parent" must bind to the focusable
             button, or keyboard focus never triggers the tooltip / aria-describedby. -->
        <v-tooltip
          activator="parent"
          location="start"
          >Ask AI Assistant</v-tooltip
        >
      </v-btn>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.ai-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  // 1900: above all page content and Vuetify layout drawers (~1004), BELOW 2000
  // where Vuetify's overlay stack starts — open menus/selects/dialogs/tooltips
  // must always cover the FAB. Fixed-layer ladder: 1900 (FAB) < 2000+ (overlays)
  // < 2400 (ai-replace modal) < 2900 GlobalLoader < 3000 RouteProgressBar
  // < 3100 toasts < 3210-3220 tour.
  z-index: 1900;

  .v-btn {
    transition: transform $duration-fast $ease-standard;

    &:hover {
      transform: scale(1.06);
    }
    &:active {
      transform: scale(0.94);
    }
  }

  &__pulse {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: rgb(var(--v-theme-secondary));
    // "Response in progress" ambient indicator — reuses the shared keyframe
    // (never declare private @keyframes; see _animations.scss header mandate).
    animation: pulse-soft 1.6s ease-in-out infinite;
  }
}
</style>
